// Nice to haves:
// - filtering by topic
// - pagination
/* eslint-disable react/prop-types,react/display-name */
import React, { Fragment } from 'react';
import { Box, Col, Heading, Row, Text } from '@fast-ai/ui-components';
import { GatsbyImage, Link, getImage } from 'gatsby-theme-fast-ai-sidebar';
import Clock from '@fast-ai/dashboard-icons/src/Clock';
import DateTime from '@fast-ai/ui/DateTime';
import List from '@fast-ai/ui/List';
import ListItem from '@fast-ai/ui/ListItem';
import Pill from '@fast-ai/ui/Pill';
// eslint-disable-next-line
import slugify from '@sindresorhus/slugify';

import { GatsbyCircleImage } from '../ui';
import { Link as IntlLink } from '../intl';

const metaItemProps = {
	icon: { size: 18, style: { marginBottom: '-2px', overflow: 'hidden' } },
	label: {
		as: 'span',
		sx: {
			ml: '4px',
			whiteSpace: 'pre-wrap',
			display: 'inline',
		},
	},
};
const styles = {
	headingsWrapper: {
		alignItems: 'center',
		flexWrap: 'wrap',
		mb: t => t.text.heading.mb,
		pt: t => t.text.heading.pt,
	},
	resetHeading: {
		mb: [0, 0, 0, 0],
	},
	pills: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 2,
		my: 2,
	},
	metadataList: {
		lineHeight: '10px',
		my: 2,
	},
	metadataItem: {
		verticalAlign: 'middle',
		m: 0,
		py: [1, 0],
		whiteSpace: 'pre-wrap',
		lineHeight: '10px!important',
	},
};
const LinkOrNotning = ({ to, children, sx }) => {
	if (to == null) {
		return children;
	}
	return (
		<Link sx={{ textDecoration: 'none', color: 'body', ...sx }} to={to}>
			{children}
		</Link>
	);
};

const ArticlePreview = ({
	to,
	title,
	subtitle,
	headingLevel = 2,
	author,
	authorImage,
	published,
	timeToRead,
	topics,
	description,
	thumbnail,
	hideMeta,
	hidePerex,
	layout = 'vertical-middle',
	size = 'normal',
	...rest
}) => {
	const [layoutOrder, textAlignProp] = layout.split('-');

	const textAlign =
		textAlignProp === 'start'
			? 'left'
			: textAlignProp === 'middle'
			? 'center'
			: null;
	const metaItems = [
		{
			key: 'author',
			title: 'Author',
			child: author ? (
				<Fragment>
					<GatsbyCircleImage
						sx={{
							maxWidth: '250px',
							display: 'inline',
							'.gatsby-image-wrapper': { mx: 'auto' },
						}}
					>
						<GatsbyImage image={getImage(authorImage)} alt={author} />
					</GatsbyCircleImage>
					<Box as="span" {...metaItemProps.label}>
						{author}
					</Box>
				</Fragment>
			) : null,
		},
		{
			key: 'published',
			title: 'Published',
			child:
				published != null ? (
					<Fragment>
						<Clock {...metaItemProps.icon} />
						<DateTime disableTime {...metaItemProps.label}>
							{new Date(published)}
						</DateTime>
					</Fragment>
				) : null,
		},
		{
			key: 'readTime',
			title: 'Read time',
			child:
				timeToRead != null ? (
					<Fragment>
						<Box as="span" {...metaItemProps.label}>
							{timeToRead} min read
						</Box>
					</Fragment>
				) : null,
		},
	];
	const thumbnailSection = thumbnail && (
		<Box
			as="figure"
			sx={{
				m: 0,
				p: 0,
				py: [2, 2, 4],

				'.gatsby-image-wrapper': {
					maxHeight: '80vh',
				},
			}}
		>
			<LinkOrNotning to={to}>
				<GatsbyImage image={getImage(thumbnail)} alt={title} />
			</LinkOrNotning>
		</Box>
	);

	const contentSection = (
		<Fragment>
			{subtitle ? (
				<hgroup>
					<Heading
						as={`h${headingLevel}`}
						variant={size === 'large' ? 'subHeading1' : 'subHeading2'}
						sx={styles.resetHeading}
					>
						<LinkOrNotning to={to}>{title}</LinkOrNotning>
					</Heading>
					<Text
						as="p"
						{...(size === 'large'
							? { variant: 'heading' }
							: { variant: 'subHeading1' })}
						sx={styles.resetHeading}
					>
						<LinkOrNotning to={to}>{subtitle}</LinkOrNotning>
					</Text>
				</hgroup>
			) : (
				<Heading
					as={`h${headingLevel}`}
					{...(size === 'large' ? {} : { variant: 'subHeading1' })}
					sx={styles.resetHeading}
				>
					<LinkOrNotning to={to}>{title}</LinkOrNotning>
				</Heading>
			)}
			{topics && topics.length ? (
				<Box sx={{ ...styles.pills, justifyContent: textAlign }}>
					{topics.map(
						({ to, name } = {}) =>
							to != null &&
							name != null && (
								<IntlLink
									key={to}
									className="pill"
									to={to}
									sx={{ textDecoration: 'none' }}
								>
									<Pill value={name} />
								</IntlLink>
							)
					)}
				</Box>
			) : null}
			{!hideMeta && (
				<List inline variant="muted" sx={styles.metadataList}>
					{metaItems.map(
						({ key, title, child }) =>
							child && (
								<ListItem key={key} title={title} sx={styles.metadataItem}>
									<Box sx={{ display: 'flex', alignItems: 'center' }}>
										{child}
									</Box>
								</ListItem>
							)
					)}
				</List>
			)}
		</Fragment>
	);

	const perexSection =
		!hidePerex && description ? <Text>{description}</Text> : null;

	const headingStyles = {
		...styles.headingsWrapper,
		textAlign,
	};

	return layoutOrder === 'vertical' ? (
		<Row as="header" sx={headingStyles} {...rest}>
			<Col span={12}>{contentSection}</Col>
			<Col span={12}>{thumbnailSection}</Col>
			{perexSection ? <Col span={12}>{perexSection}</Col> : null}
		</Row>
	) : layoutOrder === 'horizontal' ? (
		<Row as="header" sx={headingStyles} {...rest}>
			<Col span={[12, 4]}>{thumbnailSection}</Col>
			<Col span={[12, 8]}>
				{contentSection}
				{perexSection}
			</Col>
		</Row>
	) : null;
};

export default ArticlePreview;
